import { authService } from '@/services/auth.js';
import { backboneService } from '@/services/backbone.js';
import { HatElement } from '@/components/hat-element.js';

class HatLogin extends HatElement {
    private _loading = false;
    private _unsubscribe: (() => void) | null = null;

    async connectedCallback() {
    super.connectedCallback();
    this.render();
        this.setLoading(false);
        this._unsubscribe = authService.subscribe((event) => {
            if (event.status === 'authenticated') {
                backboneService.navigateTo('/app');
            }
            if (event.status === 'error') {
                this.setLoading(false);
                this.setError(event.error?.message ?? 'Authentication failed.');
            }
        }
    );

    const container = this.root.querySelector('.google-btn-container');
        await authService.renderSignInButton(container!);
        this.bindEvents();
    }

    bindEvents() {
        const form = this.root.querySelector('form')!;

        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            if (this._loading) {
                return;
            }
            const email = this.root.querySelector<HTMLInputElement>('#email')!.value.trim();
            const password = this.root.querySelector<HTMLInputElement>('#password')!.value;
            this.setError('');
            this.setLoading(true);
            try {
                await authService.signInWithEmail(email, password);
            } catch (err) {
                this.setLoading(false);
                this.setError((err as Error).message ?? 'Login failed.');
            }
        });
    }

    setLoading(loading: boolean) {
        this._loading = loading;
        const btn = this.root.querySelector<HTMLButtonElement>('.sign-in-btn');
        const email = this.root.querySelector<HTMLInputElement>('#email');
        const password = this.root.querySelector<HTMLInputElement>('#password');
        if (btn) {
            btn.disabled = loading;
            btn.textContent = loading? 'Signing In...' : 'Sign In';
        }
        if (email) { email.disabled = loading; }
        if (password) { password.disabled = loading; }
    }

    setError(message: string) {
        const error = this.root.querySelector<HTMLElement>('.error')!;
        error.textContent = message;
        error.style.display = message ? 'block' : 'none';
    }

    render() {
        this.root.innerHTML = `
            <style>
                :host {
                    display:block;
                    width:100%;
                }

                .page{
                    min-height: calc(100vh - 140px);
                    display:flex;
                    justify-content:center;
                    align-items:center;
                    padding:24px;
                    box-sizing:border-box;
                }

                .card{
                    width:100%;
                    max-width:460px;
                    background: rgba(255,255,255,0.92);
                    backdrop-filter: blur(12px);
                    border: 1px solid rgba(255,255,255,0.35);
                    border-radius:28px;
                    padding:44px 36px;

                    box-shadow:
                        0 12px 40px
                        rgba(0,0,0,0.18),
                        inset 0 1px 0
                        rgba(
                            255,255,255,0.45
                        );

                    position:relative;
                    overflow:hidden;
                }

                .card::before{
                    content:"";
                    position:absolute;
                    inset:0;

                    background:
                        radial-gradient(
                            circle at top right,
                            rgba(
                                255,255,255,0.65
                            ),
                            transparent 45%
                        );

                    pointer-events:none;
                }

                .logo{
                    width:86px;
                    height:86px;
                    margin:0 auto 24px;
                    border-radius:24px;
                    background:
                        linear-gradient(
                            135deg,
                            #1f1f1f,
                            #444
                        );
                    display:flex;
                    align-items:center;
                    justify-content:center;
                    color:white;
                    font-size:42px;
                    font-weight:700;
                }

                h1{
                    margin:0 0 12px;
                    text-align:center;
                    font-size:34px;
                    color:#111;
                }

                .subtitle{
                    margin:0 auto 34px;
                    text-align:center;
                    color:#666;
                    line-height:1.6;
                    font-size:15px;
                    max-width:320px;
                }

                form{
                    display:flex;
                    flex-direction:column;
                    gap:16px;
                }

                label{
                    display:flex;
                    flex-direction:column;
                    gap:8px;
                    font-size:14px;
                    color:#444;
                    font-weight:600;
                }

                input{
                    padding:14px 16px;
                    border-radius:14px;
                    border: 1px solid #ddd;
                    font-size:15px;
                    background:white;
                    box-sizing:border-box;
                }

                input:focus{
                    outline:none;
                    border-color:#444;
                    box-shadow:
                        0 0 0 4px
                        rgba(
                            0,0,0,0.08
                        );
                }

                .sign-in-btn{
                    margin-top:8px;
                    padding:14px;
                    border:none;
                    border-radius:16px;
                    background:
                        linear-gradient(
                            135deg,
                            #222,
                            #444
                        );
                    color:white;
                    font-size:15px;
                    font-weight:600;
                    cursor:pointer;
                }

                .sign-in-btn:hover{
                    opacity:.95;
                }

                .sign-in-btn:disabled{
                    opacity:.6;
                    cursor:wait;
                }

                .divider{
                    display:flex;
                    align-items:center;
                    gap:14px;
                    margin:30px 0 22px;
                    color:#777;
                    font-size:13px;
                }

                .divider::before,
                .divider::after{
                    content:"";
                    flex:1;
                    height:1px;
                    background:
                        rgba(
                            0,0,0,0.12
                        );
                }

                .google-btn-container{
                    display:flex;
                    justify-content:center;
                }

                .error{
                    display:none;
                    margin-top:4px;
                    color:#c62828;
                    font-size:14px;
                }

                .footer{
                    margin-top:26px;
                    text-align:center;
                    color:#777;
                    font-size:13px;
                }

            </style>
            <div class="page">
                <div class="card">
                    <div class="logo">
                        H
                    </div>

                    <form>
                        <label>
                            Email
                            <input id="email" type="email" autocomplete="email" required />
                        </label>

                        <label>
                            Password
                            <input id="password" type="password" autocomplete="current-password" required />
                        </label>
                        <div class="error"></div>
                        <button class="sign-in-btn" type="submit">Sign In</button>
                    </form>

                    <div class="divider">Or continue with</div>

                    <div class="google-btn-container"></div>
                    <div class="footer">
                        Secure authentication powered by Google and Firebase
                    </div>
                </div>
            </div>
        `;
    }

    disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribe?.();
    }
}

customElements.define('hat-login', HatLogin);